import React, { useMemo, useRef, useState } from "react";
import { Col, Stack, Container } from "react-bootstrap";
import useSortableData from "../useSortableData";
import ExportData from "../ExportData";
import moment from "moment/min/moment-with-locales";
import { useVirtualizer } from "@tanstack/react-virtual";
import ComparisonSongsHeader from "./ComparisonSongsHeader";
import ComparisonSongsRow from "./ComparisonSongsRow";
import SongSearchInput from "../SearchInput";
import { useResolvedRadioNames } from "../HelperHooks";
import EmptyRow from "../EmptyRow";
import SongCatalogueModal from "../songCatalogue/SongCatalogueModal";
import DataDescription from "../../components/DataDescription";

export default function ComparisonSongsTable({ comparisonData, songs, isCommon, radioId }) {
    const [catalogueSong, setCatalogueSong] = useState(null);
    const [filterData, setFilterData] = useState({ searchString: "" });
    const [resultCount, setResultCount] = useState(0);
    const tableContainerRef = useRef(null);
    const resolvedRadios = useResolvedRadioNames();

    const songsMemo = useMemo(() => {
        if (songs) {
            let filteredData = songs;
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.release_year?.toString().includes(filterData.searchString.trim())
                );
            }
            const enrichedData = filteredData.map(song => ({
                ...song,
                spins: song.radio_spin && song.radio_spin.length >= 2 ? (song.radio_spin[0]?.spins || 0) + (song.radio_spin[1]?.spins || 0) : song.spins || 0,
            }));

            setResultCount(enrichedData.length);

            return enrichedData;
        }
        return [];
    }, [songs, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    useMemo(() => {
        if (sortedItems) {
            setResultCount(sortedItems.length);
        }
    }, [sortedItems]);

    const csvHeaders = [
        "Rank",
        "Artist",
        "Title",
        "Release Year",
        "Spins",
        isCommon ? "Spins A" : "",
        isCommon ? "Spins B" : "",
        "First Played",
        isCommon ? "Played on" : "",
        "Item ID",
    ];
    const csvData = useMemo(() => {
        if (!sortedItems || !resolvedRadios || !comparisonData?.input?.radio_ids) return [];

        return sortedItems.map((x, i) => {
            const radioIdA = comparisonData.input.radio_ids[0];
            const radioIdB = comparisonData.input.radio_ids[1];

            const spinsA = x.radio_spin?.find(spin => spin.radio_id === radioIdA)?.spins ?? 0;
            const spinsB = x.radio_spin?.find(spin => spin.radio_id === radioIdB)?.spins ?? 0;
            const totalSpins = spinsA + spinsB || x.spins || 0;

            return [
                i + 1,
                x.artist,
                x.title,
                x.release_year,
                totalSpins,
                isCommon ? spinsA : "",
                isCommon ? spinsB : "",
                moment(x.first_play).format("L"),
                isCommon ? resolvedRadios?.find(r => r.radio_id === x.first_on)?.radio_name ?? "Unknown" : "",
                x.item_id,
            ];
        });
    }, [isCommon, resolvedRadios, sortedItems, comparisonData]);

    const virtualizer = useVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: () => 32,
        overscan: 30,
        getScrollElement: () => tableContainerRef.current,
    });

    return (
        <>
            {comparisonData && (
                <>
                    <DataDescription>
                        {isCommon ? (
                            <>
                                <DataDescription.Station label="Station (A)" radioId={comparisonData.input.radio_ids[0]} />
                                <DataDescription.Station label="Station (B)" radioId={comparisonData.input.radio_ids[1]} />
                            </>
                        ) : (
                            ""
                        )}
                        {!isCommon ? <DataDescription.Station radioId={radioId} /> : ""}
                        <DataDescription.DateRange startDate={comparisonData?.input.start_datetime} endDate={comparisonData.input?.end_datetime} />
                        <DataDescription.TimeRange startTime={comparisonData.input?.start_time} endTime={comparisonData.input?.end_time} />
                        <DataDescription.Perspective days={comparisonData.input?.days} />
                        {(comparisonData.input.min_release_year > 0 ||
                            (comparisonData.input.max_release_year > 0 && comparisonData.input.max_release_year !== new Date().getFullYear())) && (
                            <DataDescription.Simple
                                label="Release Year"
                                value={`${comparisonData.input.min_release_year} - ${comparisonData.input.max_release_year || "∞"}`}
                            />
                        )}
                        {(comparisonData.input.min_spins > 0 || comparisonData.input.max_spins > 0) && (
                            <DataDescription.Simple label="Spins" value={`${comparisonData.input.min_spins} - ${comparisonData.input.max_spins || "∞"}`} />
                        )}
                        <DataDescription.Simple label={"Results"} value={resultCount} />
                    </DataDescription>

                    <Stack direction="horizontal" gap={3} className=" mb-2 flex-wrap-reverse">
                        <Col xs={12} xl={isCommon ? 5 : true}>
                            <SongSearchInput filterData={filterData} setFilterData={setFilterData} />
                        </Col>
                        <Col xs={12} xl={"auto"} className="ms-auto order-first order-xl-last">
                            <Stack direction="horizontal" gap={1} className="ms-xl-auto w-100 w-xl-auto order-first order-xl-last">
                                <span className="me-1">Download:</span>
                                <ExportData
                                    data={csvData}
                                    headers={csvHeaders}
                                    filename={`${isCommon ? "Common" : "Unique"} Songs${
                                        !isCommon && sortedItems?.length
                                            ? ` - ${resolvedRadios?.find(r => r.radio_id === sortedItems[0].first_on)?.radio_name}`
                                            : ` - ${[...new Set(sortedItems.map(x => x.first_on))]
                                                  .map(x => resolvedRadios?.find(r => r.radio_id === x)?.radio_name)
                                                  .join("_")}`
                                    }`}
                                />
                            </Stack>
                        </Col>
                    </Stack>
                    <ComparisonSongsHeader sortConfig={sortConfig} requestSort={requestSort} showFirstOn={isCommon} />
                    <div className="overflow-y-scroll mh-500" ref={tableContainerRef}>
                        <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                            {virtualizer.getVirtualItems().map(virtualRow => {
                                const item = sortedItems[virtualRow.index];
                                if (!item) {
                                    return <EmptyRow key={virtualRow.key} text="No Songs" />;
                                }
                                return (
                                    <ComparisonSongsRow
                                        key={virtualRow.key}
                                        comparisonData={comparisonData}
                                        item={item}
                                        virtualRow={virtualRow}
                                        virtualizer={virtualizer}
                                        setCatalogueSong={setCatalogueSong}
                                        showFirstOn={isCommon}
                                    />
                                );
                            })}
                        </Container>
                    </div>
                    <SongCatalogueModal show={!!catalogueSong} onHide={() => setCatalogueSong(null)} song={catalogueSong} />
                </>
            )}
        </>
    );
}
